import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { parseCantidad, fmtNum, errorMsg } from '@/lib/recuentos';
import { fmtStock, StockLocalHeader, Leyenda } from './StockCells';

export const consolidadoAPI = {
  list: (date, warehouseId) => api.get('/despachos-consolidados', { params: { order_date: date, ...(warehouseId ? { warehouse_id: warehouseId } : {}) } }),
  create: (data) => api.post('/despachos-consolidados', data),
  update: (id, data) => api.put(`/despachos-consolidados/${id}`, data),
};

const groupBy = (items, key) => items.reduce((acc, i) => { (acc[i[key]] = acc[i[key]] || []).push(i); return acc; }, {});

const Fila = ({ it, value, onChange, editable, showStock }) => {
  const n = parseCantidad(value);
  const falta = !Number.isNaN(n) && n !== null && n < it.requested_quantity;
  const sinCentral = it.stock !== undefined && it.requested_quantity > it.stock;
  const local = fmtStock(it.stock_local);
  const localSobra = local !== null && it.stock_local >= it.requested_quantity;
  return (
    <tr className="border-t" data-testid={`consol-item-${it.item_code}`}>
      <td className="px-3 py-1.5 min-w-[160px] sticky left-0 z-[1] bg-white">
        <div className="text-slate-800">{it.item_name}</div>
        <div className="text-[11px] text-slate-400">{it.item_code} · {(it.maestros || []).map((m) => `${m.maestro} ${fmtNum(m.cantidad)}`).join(' + ')}</div>
      </td>
      <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{it.unit}</td>
      {showStock && <td className={`px-3 py-1.5 text-right whitespace-nowrap ${localSobra ? 'bg-amber-100 text-amber-800 font-semibold' : 'text-slate-600'}`} data-testid={`consol-stock-local-${it.item_code}`}>
        {local ?? <span className="text-slate-400">s/d</span>}
      </td>}
      {showStock && <td className={`px-3 py-1.5 text-right whitespace-nowrap ${sinCentral ? 'bg-red-100 text-red-700 font-semibold' : 'text-slate-600'}`} data-testid={`consol-stock-central-${it.item_code}`}>
        {fmtStock(it.stock) ?? '—'}
      </td>}
      <td className={`px-3 py-1.5 text-right font-semibold whitespace-nowrap ${sinCentral ? 'text-red-700' : ''}`}>{fmtNum(it.requested_quantity)}</td>
      <td className="px-3 py-1.5 text-right">
        <input inputMode="decimal" value={value} disabled={!editable} onChange={(e) => onChange(e.target.value)}
          className={`h-9 w-24 rounded-md border px-2 text-right font-semibold ${Number.isNaN(n) ? 'border-red-400 bg-red-50' : falta ? 'border-amber-400 bg-amber-50' : ''}`}
          data-testid={`consol-enviar-${it.item_code}`} />
      </td>
    </tr>
  );
};

export const DespachoConsolidadoDialog = ({ open, onClose, local, despacho, orderDate, onDone }) => {
  const items = useMemo(() => (despacho ? despacho.items : local?.pendiente?.items) || [], [despacho, local]);
  const [vals, setVals] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setVals(Object.fromEntries(items.map((i) => [i.item_id, fmtNum(despacho ? i.dispatched_quantity : Math.min(i.requested_quantity, Math.max(i.stock ?? i.requested_quantity, 0)))])));
    setNotes(despacho?.notes || '');
  }, [open, items, despacho]);

  const grupos = groupBy(items.map((i) => ({ ...i, category: i.category || 'Sin categoría' })), 'category');
  const detalle = despacho ? despacho.orders_detail : local?.pendiente?.orders_detail;

  const save = async () => {
    const payload = items.map((i) => ({ item_id: i.item_id, dispatched_quantity: parseCantidad(vals[i.item_id]) ?? 0 }));
    if (payload.some((p) => Number.isNaN(p.dispatched_quantity))) return toast.error('Hay cantidades inválidas');
    setSaving(true);
    try {
      if (despacho) {
        await consolidadoAPI.update(despacho.id, { items: payload, notes });
        toast.success(`Despacho N° ${despacho.numero} actualizado`);
      } else {
        const { data } = await consolidadoAPI.create({ location_id: local.location_id, order_date: orderDate, order_ids: local.pendiente.order_ids, items: payload, notes });
        toast.success(`Despacho N° ${data.numero} creado para ${local.location_name}`);
      }
      onDone();
      onClose();
    } catch (e) { toast.error(errorMsg(e, 'Error al guardar el despacho')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="consol-dialog">
        <DialogHeader>
          <DialogTitle>{despacho ? `Editar despacho N° ${despacho.numero}` : 'Despacho consolidado'} · {local?.location_name}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-600 flex flex-wrap gap-1" data-testid="consol-maestros">
          {(detalle || []).map((o) => (
            <span key={o.order_id} className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-700">{o.maestro} · {o.total_items} prod.{o.status === 'draft' ? ' (borrador)' : ''}</span>
          ))}
        </div>
        <div className="overflow-auto min-h-0 border rounded-lg">
          <table className="w-full text-sm" data-testid="consol-table">
            <thead className="bg-slate-50 text-slate-600 sticky top-0 z-10"><tr>
              <th className="px-3 py-2 text-left sticky left-0 z-[2] bg-slate-50 align-top">Producto</th>
              <th className="px-3 py-2 text-left align-top">Unidad</th>
              {!despacho && <StockLocalHeader info={local?.stock_local_bodega} />}
              {!despacho && <th className="px-3 py-2 text-right align-top">Stock Bodega Central</th>}
              <th className="px-3 py-2 text-right align-top">Pedido</th>
              <th className="px-3 py-2 text-right align-top">Cantidad a despachar</th>
            </tr></thead>
            {Object.entries(grupos).map(([cat, its]) => (
              <tbody key={cat}>
                <tr><td colSpan={despacho ? 4 : 6} className="p-0 bg-orange-50/70"><div className="sticky left-0 inline-block px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-orange-700">{cat}</div></td></tr>
                {its.map((it) => <Fila key={it.item_id} it={it} showStock={!despacho} value={vals[it.item_id] ?? ''} editable onChange={(v) => setVals((s) => ({ ...s, [it.item_id]: v }))} />)}
              </tbody>
            ))}
          </table>
        </div>
        {!despacho && <Leyenda />}
        <Textarea rows={2} placeholder="Notas del despacho (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="consol-notas" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="consol-confirmar-btn">
            {saving ? 'Guardando...' : despacho ? 'Guardar cambios' : `Confirmar despacho (${items.length} productos)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
